<script setup lang="ts">
/**
 * スレッドの内の1つの塊（クエリ, ソース, 回答の1セット）を表すコンポーネント
 */
import { computed, provide, ref, toRef } from 'vue';
import { trackingSurveyEvent } from '@/api/tracking';
import type { PageName } from '@/api/tracking';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import { DgrLoading } from '@stockmarkteam/donguri-ui';
import Divider from '@/components/common/divider.vue';
import TextTabItem from '@/components/common/molecules/text-tab-item.vue';
import { useSurveyHistoryCommon } from '@/components/survey/composables/useSurveyHistoryCommon';
import SurveyAdpDocumentCard from '@/components/survey/document/survey-adp-document-card.vue';
import SurveyReportCard from '@/components/survey/document/survey-adp-report-card.vue';
import SurveyUserDocumentCard from '@/components/survey/document/survey-user-document-card.vue';
import SurveySelectedFilters from '@/components/survey/input/survey-selected-filters.vue';
import SurveyHistoryDeep from '@/components/survey/survey-history-deep.vue';
import SurveyHistoryNormal from '@/components/survey/survey-history-normal.vue';
import SurveyQuestion from '@/components/survey/survey-question.vue';
import {
  DeepModeProcess,
  FeedbackType,
  FilterInfo,
  isReport,
  Part,
  SurveyHistoryStatus,
  SurveyRequestState,
} from '@/types';
import {
  AdpDocument,
  isUserDocument,
  SurveyPageName,
  UserDocument,
} from '@/types';
import { surveyHistorySourcesKey } from '@/utils/injectionKeys';
import { checkRequestState } from '@/utils/survey/common';
import {
  SURVEY_ERROR_MESSAGES,
  SURVEY_FEATURE_NAME,
} from '@/utils/survey/constants';
import { getDocumentUrl } from '@/utils/user-document/common';
import { featureFlags } from '@/featureFlags';

interface Props {
  sessionId: string; // surveysテーブルのuuidカラムの値 (要約画面のURLに含まれるID)。深掘り検索（同じスレッド）の間は同じID
  requestId: string; // survey_historiesテーブルのuuidカラムの値 (要約ブロックを識別するID)
  question: string;
  incomplete: boolean; // 不完全なデータかどうか (再生成ができる中途半端なデータある状態で深掘りした時に、 survey_requests → survey_histories に変換する必要があり、 その際に不完全な状態であることを示すフラグ)
  sources: Array<AdpDocument | UserDocument>;
  deepModeProcesses: Array<DeepModeProcess>;
  originMarkdownReasoning: string;
  reasoning: Array<Part>;
  originMarkdownSummary: string; // LLMの出力したマークダウン形式の要約 (DOMにパースする前の状態)
  summary: Array<Part>;
  enableAnimation: boolean;
  enableFeedback: boolean; // フィードバック可能かどうか
  enableSourceTab: boolean; // ソースタブを表示するかどうか
  feedbackType: FeedbackType | undefined; // survey_feedbacksテーブルのevaluationカラムの値 (ブロックに対して実行したフィードバックの種類)
  filterInfo: FilterInfo; // 要約ブロック生成時に選択したフィルター情報
  status: SurveyHistoryStatus;
  deepMode: boolean;
  requestState?: SurveyRequestState; // survey_requests(生成途中データ)の状態
  /* 以下は計測専用で使用 */
  pageName: PageName;
  rank?: number; // スレッド内での要約ブロックの順番 (1-indexed)
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update-document': [{ sourceIndex: number; document: AdpDocument }];
  stop: [];
  regenerate: [];
  'disable-animation': [];
}>();

type TabName = 'answer' | 'source';

/**
 * Local States
 */

const { searchResultsBreakdownMessage, dynamicTextSize, copySummary } =
  useSurveyHistoryCommon({
    sources: toRef(props, 'sources'),
    filterInfo: toRef(props, 'filterInfo'),
    question: toRef(props, 'question'),
    originMarkdownSummary: toRef(props, 'originMarkdownSummary'),
    requestId: toRef(props, 'requestId'),
    sessionId: toRef(props, 'sessionId'),
    pageName: props.pageName as SurveyPageName,
  });

const TAB_ANSWER = 'answer';
const TAB_SOURCE = 'source';
const visibleTab = ref<TabName>(TAB_ANSWER);

const SOURCE_DISPLAY_COUNT = 10;
const sourceLimit = ref<number>(SOURCE_DISPLAY_COUNT);
const visibleSources = computed(() => {
  return props.sources.slice(0, sourceLimit.value);
});

const showCopyButton = computed(() => {
  return (
    props.originMarkdownSummary.length > 0 &&
    !props.status.isError &&
    !props.status.isNoHit &&
    !props.status.isInProgress
  );
});

/**
 * ユーザーに永続的に残るデータと思われないようにする意図で
 * 通常の要約生成のエラーは、コンテナなしでエラーメッセージのみ表示
 */
const showErrorOnly = computed(() => !props.deepMode && props.status.isError);

const errorMessage = computed(
  () =>
    SURVEY_ERROR_MESSAGES[props.status.errorCause ?? ''] ??
    SURVEY_ERROR_MESSAGES.default,
);

const showStopButton = computed(() => {
  return (
    props.deepMode &&
    props.requestId &&
    checkRequestState(
      props.requestState,
      ['stopping', 'stopped', 'aborted', 'completed'],
      false,
    ) &&
    props.status.isInProgress
  );
});

const showRegenerateButton = computed(() => {
  const canRegenerate =
    props.deepMode &&
    props.requestId &&
    !props.incomplete &&
    (checkRequestState(
      props.requestState,
      ['stopping', 'stopped', 'aborted'],
      true,
    ) ||
      props.status.isError);
  return canRegenerate && !props.status.isInProgress;
});

const isRegenerateButtonDisabled = computed(() => {
  return checkRequestState(props.requestState, ['stopping'], true);
});

const providedSourceUrls = computed(() => {
  return props.sources.map(source =>
    isUserDocument(source) ? getDocumentUrl(source) : source.url,
  );
});

provide(surveyHistorySourcesKey, providedSourceUrls);

/**
 * Handlers
 */

const changeVisibleTab = (visibleState: TabName): void => {
  if (visibleState === visibleTab.value) return;

  if (visibleTab.value === TAB_ANSWER && visibleState === TAB_SOURCE) {
    sourceLimit.value = SOURCE_DISPLAY_COUNT;
  }

  emit('disable-animation');
  visibleTab.value = visibleState;
};

const handleStop = () => {
  emit('stop');

  // 計測
  trackingSurveyEvent('survey_stop', {
    rank: props.rank ?? 0,
    request_id: props.requestId,
    session_id: props.sessionId,
  });
};

const handleRegenerate = () => {
  if (isRegenerateButtonDisabled.value) return;

  emit('regenerate');

  // 計測
  trackingSurveyEvent('survey_regenerate', {
    rank: props.rank ?? 0,
    request_id: props.requestId,
    session_id: props.sessionId,
  });
};
</script>

<template>
  <template v-if="showErrorOnly">
    <p class="error-message">
      {{ errorMessage }}
    </p>
  </template>
  <div v-else class="survey-history-container">
    <SurveyQuestion
      v-if="featureFlags.ANDEV_10336_RAISE_SURVEY_PROMPT_LIMIT"
      class="survey-history-content"
      :question="props.question"
    />
    <h2 v-else class="question survey-history-content" :class="dynamicTextSize">
      {{ props.question }}
    </h2>
    <SurveySelectedFilters
      v-if="
        filterInfo.docTypes.length > 0 || filterInfo.specifiedFiles.length > 0
      "
      class="filter-tags-wrapper survey-history-content c-text-regular--s"
      :enable-delete="false"
      :doc-types="filterInfo.docTypes"
      :search-scope="filterInfo.searchScope"
      :specified-files="filterInfo.specifiedFiles"
      :group-id="filterInfo.groupId"
      :group-tag-ids="filterInfo.groupTags"
    />
    <div class="survey-history-tabs survey-history-content">
      <div class="tabs-left">
        <button class="tab-btn" @click="changeVisibleTab(TAB_ANSWER)">
          <TextTabItem
            text="回答"
            :is-selected="visibleTab === TAB_ANSWER"
            :disabled="false"
          >
            <div class="tab-text">
              <div
                v-if="props.status.isInProgress"
                class="loading-container"
                aria-busy="true"
                aria-label="Loading"
              >
                <DgrLoading />
              </div>
              <DgrIcon v-else name="sparkles-fill" :keep-fill="false" />
              回答
            </div>
          </TextTabItem>
        </button>
        <button
          v-if="
            props.enableSourceTab &&
            !props.status.isInProgress &&
            props.sources.length > 0
          "
          class="tab-btn"
          @click="changeVisibleTab(TAB_SOURCE)"
        >
          <TextTabItem
            text="ソース"
            :is-selected="visibleTab === TAB_SOURCE"
            :disabled="false"
            :count="props.sources.length"
          >
            <div class="tab-text">
              <DgrIcon name="search" :keep-fill="false" />
              ソース
            </div>
          </TextTabItem>
        </button>
      </div>
      <div v-if="props.deepMode" class="tabs-right action-buttons-wrapper">
        <button
          v-if="showStopButton"
          @click="handleStop"
          class="action-button c-btn--small"
        >
          <DgrIcon
            name="square-fill"
            size="small"
            :keep-fill="false"
            class="shrink-guard"
          />
          <span>生成を停止</span>
        </button>
        <button
          v-else-if="showRegenerateButton"
          @click="handleRegenerate"
          :disabled="isRegenerateButtonDisabled"
          class="action-button c-btn--small"
        >
          <DgrIcon
            name="sync"
            size="small"
            :keep-fill="false"
            class="shrink-guard"
          />
          <span>再生成</span>
        </button>
      </div>
    </div>
    <Divider />
    <div v-if="visibleTab === TAB_ANSWER" class="answer-tab">
      <SurveyHistoryNormal
        v-if="!props.deepMode"
        :session-id="props.sessionId"
        :request-id="props.requestId"
        :question="props.question"
        :sources="props.sources"
        :origin-markdown-reasoning="props.originMarkdownReasoning"
        :reasoning="props.reasoning"
        :origin-markdown-summary="props.originMarkdownSummary"
        :summary="props.summary"
        :enable-animation="props.enableAnimation"
        :enable-feedback="props.enableFeedback"
        :feedback-type="props.feedbackType"
        :status="props.status"
        :request-state="props.requestState"
        :page-name="props.pageName"
      />
      <SurveyHistoryDeep
        v-else
        :session-id="props.sessionId"
        :request-id="props.requestId"
        :question="props.question"
        :incomplete="props.incomplete"
        :sources="props.sources"
        :deep-mode-processes="props.deepModeProcesses"
        :origin-markdown-reasoning="props.originMarkdownReasoning"
        :reasoning="props.reasoning"
        :origin-markdown-summary="props.originMarkdownSummary"
        :summary="props.summary"
        :enable-animation="props.enableAnimation"
        :enable-feedback="props.enableFeedback"
        :feedback-type="props.feedbackType"
        :status="props.status"
        :request-state="props.requestState"
        :page-name="props.pageName"
        @stop="emit('stop')"
        @regenerate="emit('regenerate')"
      />
      <template v-if="showCopyButton">
        <Divider margin="16px 0 8px" />
        <div class="action-buttons-wrapper survey-history-content">
          <button
            @click="copySummary"
            class="action-button c-title-bold--m c-btn--small"
          >
            <DgrIcon name="copy" />
            <p>コピーする</p>
          </button>
        </div>
      </template>
      <template v-else>
        <div class="spacing-08"></div>
      </template>
    </div>
    <div v-else-if="visibleTab === TAB_SOURCE" class="source-tab">
      <div
        v-if="props.sources.length > 0 && !props.status.isInProgress"
        class="survey-history-content document-cards"
      >
        <span class="searching-word c-text-regular--xm">{{
          searchResultsBreakdownMessage
        }}</span>
        <div class="document-cards-grid">
          <div v-for="(document, index) in visibleSources" :key="document.id">
            <SurveyUserDocumentCard
              v-if="isUserDocument(document)"
              :user-document="document"
              :badge-number="index + 1"
              :page-name="SURVEY_FEATURE_NAME"
              :feature="SURVEY_FEATURE_NAME"
              :rank-in-whole-feed="index + 1"
              :contents-context="{
                event_name: SURVEY_FEATURE_NAME,
                session_id: props.sessionId,
              }"
            />
            <SurveyReportCard
              v-else-if="isReport(document)"
              :adp-report="document"
              :badge-number="index + 1"
              :page-name="props.pageName"
              :feature="SURVEY_FEATURE_NAME"
              :rank-in-whole-feed="index + 1"
              :contents-context="{
                event_name: SURVEY_FEATURE_NAME,
                session_id: props.sessionId,
              }"
              @update-document="
                document =>
                  emit('update-document', { sourceIndex: index, document })
              "
            />
            <SurveyAdpDocumentCard
              v-else
              :adp-document="document"
              :badge-number="index + 1"
              :is-narrow="true"
              :page-name="props.pageName"
              :feature="SURVEY_FEATURE_NAME"
              :rank-in-whole-feed="index + 1"
              :contents-context="{
                event_name: SURVEY_FEATURE_NAME,
                session_id: props.sessionId,
              }"
              @update-document="
                document =>
                  emit('update-document', { sourceIndex: index, document })
              "
            />
          </div>
        </div>
        <div
          v-if="sourceLimit < props.sources.length"
          class="survey-history-content"
        >
          <button
            @click="sourceLimit += SOURCE_DISPLAY_COUNT"
            class="more-button c-btn c-btn--auto c-btnOutline c-text-regular--m"
          >
            さらに表示
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.survey-history-container {
  background-color: $color-white;
  padding: 16px 0 0;
  border-radius: 4px;
  border: solid 1px $color-gray400;
}

.question {
  white-space: pre-line;
}

.survey-history-content {
  padding: 0 16px;
}

.filter-tags-wrapper {
  margin: 16px 0;
}

.filter-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.survey-history-tabs {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.loading-container {
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  width: 24px;
}

.tabs-left {
  display: flex;
  gap: 8px;
}

.tabs-right {
  margin-right: 16px;
}

.tab-btn {
  background-color: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  appearance: none;
  height: auto;
}

.tab-text {
  display: flex;
  align-items: center;
  gap: 8px;
}

.answer-tab {
  margin: 16px 0 8px;
}

.source-tab {
  margin: 16px 0;
}

.searching-word {
  margin-bottom: 8px;
}

.document-cards {
  display: flex;
  flex-direction: column;
}

.document-cards-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}

.document-cards-grid > div {
  display: flex;
  align-items: stretch;
}

.more-button {
  display: block;
  margin: 8px auto 0 auto;
  cursor: pointer;
}

.action-buttons-wrapper {
  display: grid;
  gap: 16px;
  align-items: center;

  .action-button {
    display: flex;
    gap: 4px;
    align-items: center;
    width: fit-content;
    border: none;
    background: transparent;
    cursor: pointer;

    &:hover {
      background-color: $color-gray400;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;

      &:hover {
        background: transparent;
      }
    }
  }
}

.error-message {
  margin: 16px 0;
  padding-bottom: 16px;
  border-bottom: 1px solid $color-gray400;
}
</style>
